import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import { Col, Form, Row } from "react-bootstrap";
import { scrollToTop } from "utils/dom";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { accountConstants, userConstants } from "actions/types";
import { AGREEMENTS } from "statics/onboardingSteps";
import { userService } from "services/userService";
import { ROTH_IRA, TRADITIONAL_AND_ROTH_IRA } from "statics/accountTypes";
import { some, toLower } from "lodash";
import {
  activeRolloverAccountSameAsCurrent,
  activeRolloverIsMixed,
  isPaychexSubsequentAccountSelector,
  newActiveRolloverAccount,
  userIs401kRollIn,
  userIsPaychexPerks,
} from "store/selectors/user";
import {
  getUserState,
  updateIraAccountTermsAndSubmit,
  updatePaychexAccountTermsAndSubmit,
  updateRolloverAccountTermsAndSubmit,
} from "actions/userActions";

import CancelRolloverModal from "../../dashboards/individualDashboard/dashboard/CancelRolloverModal";
import ProgressBar from "components/ProgressBar";
import CircularProgressBar from "components/CircularProgressBar";
import IconSpinner from "components/IconSpinner";
import TermsModal from "pages/signUp/TermsModal";
import Button from "components/Button";
import Alert from "components/Alert";

import "./IndividualEnroll.scss";
import moment from "moment";
import type { RootState } from "types/state";

const actions = [
  userConstants.USER_ON_BOARDING_INVESTMENT_TERMS_UPDATE,
  userConstants.UPDATE_ROLLOVER_ACCOUNT_TERMS_SUBMIT,
  userConstants.UPDATE_PAYCHEX_ACCOUNT_TERMS_SUBMIT,
  userConstants.USER_STATE,
  accountConstants.GET_ACCOUNTS,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    // user.userState is shallow-typed as `object` in RootState (Redux layer
    // scheduled for deletion) — cast at the boundary to read the nested `state`
    // field
    userState: (state.user.userState as { state: string }).state,
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    isRolloverAccount: newActiveRolloverAccount(state),
    administrators: state.employer.administrators,
    isRolloverSameAsCurrent: activeRolloverAccountSameAsCurrent(state),
    isRolloverIsMixed: activeRolloverIsMixed(state),
    is401kRollin: userIs401kRollIn(state),
    isPaychexPerks: userIsPaychexPerks(state),
    isPaychexSubsequentAccount: isPaychexSubsequentAccountSelector(state),
  };
};
const mapDispatchToProps = {
  updateIraAccountTermsAndSubmit,
  updateRolloverAccountTermsAndSubmit,
  updatePaychexAccountTermsAndSubmit,
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  showCancelRolloverModal: boolean;
  accountAgreement: boolean;
  termsOfService: boolean;
  iraAgreement: boolean;
  finalAgreement: boolean;
  crbAgreement: boolean;
  submitted: boolean;
  showAcctAgreement: boolean;
  showIraAgreement: boolean;
  showTermsOfService: boolean;
  rolloverTerms: boolean;
  showRolloverTerms: boolean;
  virtualAccountNumberDisclosure: boolean;
  showVirtualAccountNumberDisclosure: boolean;
  rothAccountAgreement: boolean;
  showRothAccountAgreement: boolean;
  showCRBAgreement: boolean;
  loading: boolean;
  // set in componentDidMount via setState; absent from the initial state
  accountType?: string | null;
}

class IndividualTerms extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showCancelRolloverModal: false,
      accountAgreement: false,
      termsOfService: false,
      iraAgreement: false,
      finalAgreement: false,
      crbAgreement: false,
      submitted: false,
      showAcctAgreement: false,
      showIraAgreement: false,
      showTermsOfService: false,
      rolloverTerms: false,
      showRolloverTerms: false,
      virtualAccountNumberDisclosure: false,
      showVirtualAccountNumberDisclosure: false,
      rothAccountAgreement: false,
      showRothAccountAgreement: false,
      showCRBAgreement: false,
      loading: true,
    };
  }

  componentDidMount() {
    void userService
      .getIraAccountForm(this.props.client as ApolloClient<object>)
      .then(
        (data) => {
          this.setState({
            // preserve the original `.` access (throws if account is null)
            accountType: data.account!.type,
            loading: false,
          });
        },
        () => {
          this.setState({
            loading: false,
          });
        }
      );
    scrollToTop();
    window.analytics.page("Individual Terms & Agreements");
  }

  submitTerms = () => {
    let terms: {
      accountAgreement: boolean | null;
      termsOfService: boolean | null;
      rolloverTerms?: boolean;
    };

    if (this.props.isRolloverAccount || this.props.isRolloverIsMixed) {
      terms = {
        accountAgreement: this.state.accountAgreement,
        termsOfService: this.state.termsOfService,
        rolloverTerms: this.state.rolloverTerms,
      };

      void this.props.updateRolloverAccountTermsAndSubmit(
        this.props.client as ApolloClient<object>,
        // PRESERVED BUG (conversion-only): rollover terms may carry null
        // accountAgreement/termsOfService (same-as-current branch), but the
        // mutation variables require boolean. Passing as-is to preserve runtime.
        terms as Parameters<Props["updateRolloverAccountTermsAndSubmit"]>[1]
      );

      if (this.props.isRolloverIsMixed) {
        window.analytics.track(
          "Submitted Traditional & Roth Rollover Terms & Agreements"
        );
      } else {
        window.analytics.track("Submitted Rollover Terms & Agreements");
      }

      this.setState({
        submitted: true,
      });
    } else if (this.props.isRolloverSameAsCurrent) {
      terms = {
        accountAgreement: null,
        termsOfService: null,
        rolloverTerms: this.state.rolloverTerms,
      };

      void this.props.updateRolloverAccountTermsAndSubmit(
        this.props.client as ApolloClient<object>,
        // PRESERVED BUG (conversion-only): rollover terms may carry null
        // accountAgreement/termsOfService (same-as-current branch), but the
        // mutation variables require boolean. Passing as-is to preserve runtime.
        terms as Parameters<Props["updateRolloverAccountTermsAndSubmit"]>[1]
      );

      window.analytics.track(
        "Submitted Individual Rollover Terms & Agreements"
      );

      this.setState({
        submitted: true,
      });
    } else if (this.props.isPaychexSubsequentAccount) {
      // never updateIraAccountTermsAndSubmit — that submits the whole signup form
      terms = {
        accountAgreement: this.state.accountAgreement,
        termsOfService: this.state.termsOfService,
      };

      void this.props.updatePaychexAccountTermsAndSubmit(
        this.props.client as ApolloClient<object>,
        terms as Parameters<Props["updatePaychexAccountTermsAndSubmit"]>[1]
      );

      window.analytics.track("Submitted Paychex IRA Terms & Agreements");

      this.setState({
        submitted: true,
      });
    } else {
      terms = {
        accountAgreement: this.state.accountAgreement,
        termsOfService: this.state.termsOfService,
      };
      void this.props
        .updateIraAccountTermsAndSubmit(
          this.props.client as ApolloClient<object>,
          terms as Parameters<Props["updateIraAccountTermsAndSubmit"]>[1]
        )
        .then(() => {
          void this.props.getUserState(
            this.props.client as ApolloClient<object>
          );
        });
      window.analytics.track("Submitted Individual Terms & Agreements", {
        userState: this.props.userState,
      });

      this.setState({
        submitted: true,
      });
    }
  };

  getNeededAgreements = (): (keyof State)[] => {
    if (this.props.isRolloverSameAsCurrent) {
      return ["rolloverTerms", "finalAgreement"];
    } else if (this.props.isRolloverAccount) {
      return [
        "accountAgreement",
        "iraAgreement",
        "termsOfService",
        "rolloverTerms",
        "finalAgreement",
      ];
    } else if (this.props.is401kRollin) {
      const basicAgreements: (keyof State)[] = [
        "accountAgreement",
        "iraAgreement",
        "termsOfService",
        "finalAgreement",
        "virtualAccountNumberDisclosure",
      ];

      if (this.state.accountType === TRADITIONAL_AND_ROTH_IRA) {
        basicAgreements.push("rothAccountAgreement");
      }
      return basicAgreements;
    } else {
      return [
        "accountAgreement",
        "iraAgreement",
        "termsOfService",
        "finalAgreement",
        "crbAgreement",
      ];
    }
  };

  calcButtonDisabled = () => {
    const neededAgreements = this.getNeededAgreements();
    return some(neededAgreements, (agreement) => {
      return !this.state[agreement];
    });
  };

  _handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // dynamic state key keyed by input name — preserve the original
    // computed-key setState behavior
    this.setState({
      [e.target.name]: e.target.checked,
    } as unknown as Pick<State, keyof State>);
  };

  _onCancel = () => {
    this.setState({
      showCancelRolloverModal: true,
    });
  };

  _iAgreeTerms = () => {
    let terms: React.ReactNode;

    if (this.props.isRolloverSameAsCurrent) {
      terms = (
        <>
          <p
            style={{
              fontStyle: "italic",
              fontSize: 14,
              paddingTop: 10,
              paddingLeft: 4,
              color: "#0a2540",
            }}
            className="terms-text"
          >
            By selecting &ldquo;Agree&rdquo; you confirm that you have read and
            understood the rules and conditions related to your rollover.
          </p>
        </>
      );
    } else if (this.props.isRolloverAccount) {
      terms = (
        <>
          <p
            style={{
              fontStyle: "italic",
              fontSize: 14,
              paddingTop: 10,
              paddingLeft: 4,
              color: "#0a2540",
            }}
            className="terms-text"
          >
            By checking &ldquo;Agree&rdquo; you are acknowledging that you have
            read and understood all of the listed Agreements and Applications,
            as well as the Icon
            <a
              href="https://iconsavingsplan.com/terms"
              className="modal-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            .
          </p>
        </>
      );
      // Paychex Flex (Perks) users carry PAYCHEX_OAUTH (so is401kRollin is
      // true) and get the same agreement set as a 401k roll-in (Virtual Account
      // Number Disclosure, no CRB) — but they fund a standard IRA via payroll
      // deduction, not a rollover, so the "$20.00 one-time processing fee to set
      // up your account" does NOT apply to them. Excluding them here drops them
      // to the generic acknowledgment copy (else branch) with no fee sentence.
      // Unless the account they are opening IS rollover-funded, which a Perks
      // member's is once they link a 401(k) — then the fee applies.
    } else if (
      this.props.is401kRollin &&
      (!this.props.isPaychexPerks || this.props.isPaychexSubsequentAccount)
    ) {
      terms = (
        <>
          <p
            style={{
              fontStyle: "italic",
              fontSize: 14,
              paddingTop: 10,
              paddingLeft: 4,
              color: "#0a2540",
            }}
            className="terms-text"
          >
            By checking &ldquo;Agree&rdquo; you are acknowledging that you have
            read and understood all of the listed Agreements and Applications,
            as well as the Icon
            <a
              href="https://iconsavingsplan.com/terms"
              className="modal-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            . There will be a one-time processing fee of $20.00 to set up your
            account.
          </p>
        </>
      );
    } else {
      terms = (
        <>
          <p
            style={{
              fontStyle: "italic",
              fontSize: 14,
              paddingTop: 10,
              paddingLeft: 4,
              color: "#0a2540",
            }}
            className="terms-text"
          >
            By checking &ldquo;Agree&rdquo; you are acknowledging that you have
            read and understood all of the listed Agreements and Applications,
            as well as the Icon
            <a
              href="https://iconsavingsplan.com/terms"
              className="modal-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            .
          </p>
        </>
      );
    }

    return terms;
  };

  render() {
    if (this.state.loading) {
      return <IconSpinner centered />;
    }
    return this.renderForm();
  }

  renderForm = () => {
    var articleStyle = {
      paddingBottom: 0,
    };

    const rolloverTerms =
      this.props.isRolloverAccount ||
      this.props.isRolloverSameAsCurrent ||
      this.props.isRolloverIsMixed;

    const showCancelRollover =
      this.props.isRolloverAccount || this.props.isRolloverSameAsCurrent;
    const isTraditionalAndRothSignUp =
      this.state.accountType === TRADITIONAL_AND_ROTH_IRA;
    const accountAgreement =
      // accountType may be null at runtime; lodash toLower coerces it like
      // undefined to "" — cast to satisfy the string-only signature
      toLower(this.state.accountType as string | undefined) ===
        toLower(ROTH_IRA) && !isTraditionalAndRothSignUp
        ? "accountAgreementRoth"
        : "accountAgreement";

    const title = this.props.isRolloverSameAsCurrent
      ? "Rules and Conditions."
      : "Terms and Agreements.";

    const subtext = this.props.isRolloverSameAsCurrent
      ? "Please review the following IRA contribution rules and conditions, acknowledge them with your e-signature, and then submit your rollover."
      : "Please review and e-sign all IRA and partner agreements, then submit your application.";

    const terms = this._iAgreeTerms();

    let progPercent: string;
    if (this.props.isRolloverAccount) {
      progPercent = "80";
    } else if (this.props.isRolloverSameAsCurrent) {
      progPercent = "60";
    } else {
      progPercent = "95";
    }

    return (
      <div id="individual-terms">
        <ProgressBar
          isEmployer={false}
          activeStepId={AGREEMENTS.id}
          progressPercent={progPercent}
        />

        {this.state.showCancelRolloverModal && (
          <CancelRolloverModal
            show={this.state.showCancelRolloverModal}
            onClose={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
            onSuccess={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
          />
        )}
        <Form noValidate>
          <TermsModal
            type={accountAgreement}
            show={this.state.showAcctAgreement}
            onClose={() => this.setState({ showAcctAgreement: false })}
          />
          {isTraditionalAndRothSignUp && (
            <TermsModal
              type={"accountAgreementRoth"}
              show={this.state.showRothAccountAgreement}
              onClose={() => this.setState({ showRothAccountAgreement: false })}
            />
          )}
          <TermsModal
            type="iraAgreement"
            show={this.state.showIraAgreement}
            onClose={() => this.setState({ showIraAgreement: false })}
          />
          <TermsModal
            type="termsOfService"
            show={this.state.showTermsOfService}
            onClose={() => this.setState({ showTermsOfService: false })}
          />
          <TermsModal
            type="rolloverTerms"
            show={this.state.showRolloverTerms}
            onClose={() => this.setState({ showRolloverTerms: false })}
          />
          <TermsModal
            type="crbAgreement"
            show={this.state.showCRBAgreement}
            onClose={() => this.setState({ showCRBAgreement: false })}
            termsInfo={{ effectiveDate: moment().format("MMMM D, YYYY") }}
          />
          <TermsModal
            type="virtualAccountNumberDisclosure"
            show={this.state.showVirtualAccountNumberDisclosure}
            onClose={() =>
              this.setState({ showVirtualAccountNumberDisclosure: false })
            }
          />

          <div className="mega-container">
            <div className="step-container is-active" data-circle-percent="100">
              <section className="page-title-wrap">
                <article className="text-cell">
                  <p className="page-eyebrow">AGREEMENTS</p>
                  <p className="page-title">{title}</p>
                  <p className="page-subtext">{subtext}</p>
                </article>
                <article className="progress-cell">
                  <ul className="circular-progress-wrap">
                    <CircularProgressBar
                      strokeWidth="8"
                      sqSize="75"
                      percentage="90"
                    />
                  </ul>
                </article>
              </section>

              <div id="form-employer-company-profile">
                <section className="form-sec-2col">
                  <article className="col-form" style={articleStyle}>
                    {!this.props.isRolloverSameAsCurrent && (
                      <>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            controlId="formBasicAccountAgreement"
                          >
                            <Form.Check
                              type="checkbox"
                              id="accountAgreement"
                              name="accountAgreement"
                              label="Customer Account Agreement"
                              // @ts-expect-error pre-existing: boolean passed to
                              // Form.Check `value` (expects string|number) —
                              // inert coercion, preserved as-is
                              value={this.state.accountAgreement}
                              onChange={this._handleCheckBoxChange}
                            />
                            <div
                              className="modal-link"
                              onClick={() => {
                                this.setState({
                                  showAcctAgreement: true,
                                });
                                window.analytics.track(
                                  "Viewed Account Agreement"
                                );
                              }}
                            >
                              (View Agreement)
                            </div>
                          </Form.Group>
                        </Row>
                        {isTraditionalAndRothSignUp && (
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              controlId="formBasicRothAccountAgreement"
                            >
                              <Form.Check
                                type="checkbox"
                                id="rothAccountAgreement"
                                name="rothAccountAgreement"
                                label="Customer Roth Account Agreement"
                                // @ts-expect-error pre-existing: boolean passed
                                // to Form.Check `value` (expects string|number)
                                // — inert coercion, preserved as-is
                                value={this.state.rothAccountAgreement}
                                onChange={this._handleCheckBoxChange}
                              />
                              <div
                                className="modal-link"
                                onClick={() => {
                                  this.setState({
                                    showRothAccountAgreement: true,
                                  });
                                  window.analytics.track(
                                    "Viewed Roth Account Agreement"
                                  );
                                }}
                              >
                                (View Agreement)
                              </div>
                            </Form.Group>
                          </Row>
                        )}
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            controlId="formBasicIraAgreement"
                          >
                            <Form.Check
                              type="checkbox"
                              id="iraAgreement"
                              name="iraAgreement"
                              label="New IRA Account Application Agreement"
                              // @ts-expect-error pre-existing: boolean passed to
                              // Form.Check `value` (expects string|number) —
                              // inert coercion, preserved as-is
                              value={this.state.iraAgreement}
                              onChange={this._handleCheckBoxChange}
                            />
                            <div
                              className="modal-link"
                              onClick={() => {
                                this.setState({
                                  showIraAgreement: true,
                                });
                                window.analytics.track("Viewed IRA Agreement");
                              }}
                            >
                              (View Agreement)
                            </div>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            controlId="formBasictermsOfService"
                          >
                            <Form.Check
                              type="checkbox"
                              id="termsOfService"
                              name="termsOfService"
                              label="Icon Account Agreement"
                              // @ts-expect-error pre-existing: boolean passed to
                              // Form.Check `value` (expects string|number) —
                              // inert coercion, preserved as-is
                              value={this.state.termsOfService}
                              onChange={this._handleCheckBoxChange}
                            />
                            <div
                              className="modal-link"
                              onClick={() => {
                                this.setState({
                                  showTermsOfService: true,
                                });
                                window.analytics.track(
                                  "Viewed Terms Of Service"
                                );
                              }}
                            >
                              (View Agreement)
                            </div>
                          </Form.Group>
                        </Row>
                        {!this.props.is401kRollin && (
                          <Row className="mb-3">
                            <Form.Group
                              as={Col}
                              sm={12}
                              controlId="formBasicCRBAgreement"
                            >
                              <Form.Check
                                type="checkbox"
                                id="crbAgreement"
                                name="crbAgreement"
                                label="Cross River Bank Deposit Account Agreement"
                                // @ts-expect-error pre-existing: boolean passed
                                // to Form.Check `value` (expects string|number)
                                // — inert coercion, preserved as-is
                                value={this.state.crbAgreement}
                                onChange={this._handleCheckBoxChange}
                              />
                              <div
                                className="modal-link"
                                onClick={() => {
                                  this.setState({
                                    showCRBAgreement: true,
                                  });
                                  window.analytics.track(
                                    "Viewed CRB Agreement"
                                  );
                                }}
                              >
                                (View Agreement)
                              </div>
                            </Form.Group>
                          </Row>
                        )}
                      </>
                    )}

                    {rolloverTerms && (
                      <Row className="mb-3">
                        <Form.Group as={Col} controlId="formBasicRolloverTerms">
                          <Form.Check
                            type="checkbox"
                            id="rolloverTerms"
                            name="rolloverTerms"
                            label="Rollover Account Agreement"
                            // @ts-expect-error pre-existing: boolean passed to
                            // Form.Check `value` (expects string|number) — inert
                            // coercion, preserved as-is
                            value={this.state.rolloverTerms}
                            onChange={this._handleCheckBoxChange}
                          />
                          <div
                            className="modal-link"
                            onClick={() => {
                              this.setState({
                                showRolloverTerms: true,
                              });
                              window.analytics.track(
                                "Viewed Rollover Agreement"
                              );
                            }}
                          >
                            (View Agreement)
                          </div>
                        </Form.Group>
                      </Row>
                    )}
                    {this.props.is401kRollin && (
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          sm={12}
                          controlId="formBasicVirtualAccountNumberDisclosure"
                        >
                          <Form.Check
                            type="checkbox"
                            id="virtualAccountNumberDisclosure"
                            name="virtualAccountNumberDisclosure"
                            label="Virtual Account Number Disclosure"
                            // @ts-expect-error pre-existing: boolean passed to
                            // Form.Check `value` (expects string|number) — inert
                            // coercion, preserved as-is
                            value={this.state.virtualAccountNumberDisclosure}
                            onChange={this._handleCheckBoxChange}
                          />
                          <div
                            className="modal-link"
                            onClick={() => {
                              this.setState({
                                showVirtualAccountNumberDisclosure: true,
                              });
                              window.analytics.track(
                                "Viewed Virtual Account Number Disclosure"
                              );
                            }}
                          >
                            (View Agreement)
                          </div>
                        </Form.Group>
                      </Row>
                    )}
                  </article>
                </section>
                <section className="form-sec-2col">
                  <article className="col-form">
                    <div
                      className="submit-row"
                      style={{
                        borderTop: "1px solid #c7c7c7",
                      }}
                    >
                      <Row className="mb-3">
                        <Form.Group
                          as={Col}
                          controlId="formBasicFinalAgreement"
                        >
                          <Form.Check
                            type="checkbox"
                            id="finalAgreement"
                            name="finalAgreement"
                            label="Agree"
                            // @ts-expect-error pre-existing: boolean passed to
                            // Form.Check `value` (expects string|number) — inert
                            // coercion, preserved as-is
                            value={this.state.finalAgreement}
                            onChange={this._handleCheckBoxChange}
                          />
                          {terms}
                        </Form.Group>
                      </Row>
                      {this.props.error && (
                        <Alert type="error" msg={this.props.error} />
                      )}
                    </div>

                    <div className="submit-row btn-row">
                      {showCancelRollover && (
                        <Button
                          type="button"
                          btnLabel={"Cancel"}
                          color={"cancel"}
                          name="cancel"
                          onClick={this._onCancel}
                        />
                      )}
                      <Button
                        disabled={this.calcButtonDisabled()}
                        withArrow={true}
                        onClick={this.submitTerms}
                        name="submit"
                        btnLabel="Submit"
                        type="button"
                        loading={this.props.isFetching}
                      />
                    </div>
                  </article>
                </section>
              </div>
            </div>
          </div>
        </Form>
      </div>
    );
  };
}

export default connector(withApollo<Props>(IndividualTerms));
